import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Result } from 'antd';
import { usePermission } from '../../hooks/common/usePermission.ts';
import type { ReactNode } from 'react';
import { routePermissions } from './internalRoutePermission.tsx';

interface RoleGuardProps {
  /** One or more roles required (user must have at least one) */
  requiredRoles: string[];
  /** Optional children – if omitted an <Outlet /> is rendered for nested routes */
  children?: ReactNode;
  /** If true, show a 403 result page instead of redirecting */
  showForbidden?: boolean;
}

/**
 * Route guard that checks the user has at least one of the required roles.
 * When used as a layout route element (no children), renders an <Outlet />.
 */
export default function RoleGuard({
  requiredRoles,
  children,
  showForbidden = false,
}: RoleGuardProps) {
  const { checkRoles, hasPermission, hasAccessProfile } = usePermission();
  const { pathname } = useLocation();
  const allowed = hasAccessProfile ? routePermissions(pathname).some((permission) => hasPermission(permission)) : checkRoles(requiredRoles);

  if (!allowed) {
    if (showForbidden) {
      return (
        <Result
          status="403"
          title="Access Denied"
          subTitle="You do not have permission to view this page."
        />
      );
    }
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
